'use client';

import { useState } from 'react';

import { Check, ChevronDown, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import type { NoInteraction } from '../types';

interface SafePairsSectionProps {
  safePairs: NoInteraction[];
}

export function SafePairsSection({ safePairs }: SafePairsSectionProps) {
  const { t } = useTranslation('interactions');
  const [isExpanded, setIsExpanded] = useState(false);

  if (safePairs.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border-2 border-zinc-300 bg-zinc-50 shadow-[2px_2px_0_0_#d4d4d8] dark:border-zinc-500 dark:bg-zinc-800/50 dark:shadow-[2px_2px_0_0_#71717a]">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between rounded-md px-4 py-3 text-left transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-700/50"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-green-500 dark:text-green-400" />
          <span className="font-medium text-zinc-700 dark:text-zinc-300">
            {t('safePairs.title', { count: safePairs.length })}
          </span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-zinc-500 transition-transform duration-200 dark:text-zinc-400',
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      <div
        className={cn(
          'overflow-hidden border-zinc-200 transition-all duration-200 dark:border-zinc-700',
          isExpanded
            ? 'max-h-[5000px] border-t p-4 opacity-100'
            : 'max-h-0 opacity-0',
        )}
        aria-hidden={!isExpanded}
      >
        <div className="space-y-2">
          {safePairs.map((pair, index) => (
            <SafePairItem key={index} pair={pair} />
          ))}
        </div>

        <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
          {t('disclaimer.notSafe')}
          <br /> {t('disclaimer.clinicalJudgement')}
        </p>
      </div>
    </div>
  );
}

function SafePairItem({ pair }: { pair: NoInteraction }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-white/50 px-3 py-2 dark:bg-zinc-900/50">
      <Check className="h-4 w-4 shrink-0 text-green-500 dark:text-green-400" />
      <div className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="font-medium text-zinc-700 dark:text-zinc-300">
          {pair.pair.drug1.productName}
        </span>
        <span className="text-zinc-400 dark:text-zinc-500">+</span>
        <span className="font-medium text-zinc-700 dark:text-zinc-300">
          {pair.pair.drug2.productName}
        </span>
      </div>
    </div>
  );
}
